import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body2, Button, Checkbox, Dialog, Label } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { APPEND_TO_PARENT_ERROR, APPEND_TO_PARENT_SUCCESS } from "~/store/action_types";
import { appendToParent } from "~/store/actions/actions.comments";
import { getNormalAttachmentsByIds } from "~/store/selectors/selector.attachments";
import { getCommentById } from "~/store/selectors/selector.comments";
import { showErrorToast, showSuccessToast } from "../Toast";

export const AppendDialog = ({ open, onConfirm, onCancel, id }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const commentObj = useSelector((state) => getCommentById(state, id));

  const comment = commentObj ? commentObj.toJS() : {};
  const hasBody = comment?.body?.length > 0;
  const attachments = useSelector((state) => getNormalAttachmentsByIds(state, comment?.attachments));

  const hasAttachments = attachments.size > 0;

  const [form, setForm] = useState(() => ({
    append_body: hasBody,
    append_attachments: hasAttachments,
  }));

  const onChange = useCallback((e) => {
    setForm((prev) => {
      return { ...prev, [e.target.name]: e.target.checked };
    });
  }, []);

  const onAppend = useCallback(() => {
    dispatch(appendToParent(id, form)).then((action) => {
      if (action.type === APPEND_TO_PARENT_SUCCESS) {
        const { response = {} } = action;
        const { single = {} } = response;
        let message;
        switch (single.class) {
          case "Task":
            message = t("Task updated");
            break;
          case "Discussion":
            message = t("Discussion updated");
            break;
          case "Note":
            message = t("Note updated");
            break;
          case "CalendarEvent":
            message = t("Event updated");
            break;
        }
        showSuccessToast(message);
      } else if (action.type === APPEND_TO_PARENT_ERROR) {
        showErrorToast(t("Failed to update task"));
      }
      onConfirm();
    });
  }, [dispatch, form, id, onConfirm, t]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
    >
      <Dialog.Title>
        {t("Append to {{context}}", {
          context: comment.parent_type === "CalendarEvent" ? "Event" : comment.parent_type,
        })}
      </Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        <Body2 color="primary">
          {t("Chose what to append to the {{context}} description", {
            context: comment.parent_type === "CalendarEvent" ? "event" : comment.parent_type?.toLowerCase(),
          })}
          :
        </Body2>
        <div className="tw-flex tw-gap-2 tw-items-center tw-mt-2">
          <Checkbox
            id="append-text"
            name="append_body"
            onChange={onChange}
            checked={form.append_body}
            className="tw-m-1"
            disabled={!hasBody}
          />
          <Label
            size="regular"
            className={classNames("tw-self-center tw-select-none", {
              "tw-cursor-pointer": hasBody,
              "tw-opacity-50": !hasBody,
            })}
            htmlFor={hasBody ? "append-text" : undefined}
            weight="regular"
          >
            {t("Text")}
          </Label>
        </div>
        <div className="tw-flex tw-gap-2 tw-items-center tw-mt-2">
          <Checkbox
            id="append-attachments"
            name="append_attachments"
            onChange={onChange}
            checked={form.append_attachments}
            disabled={!hasAttachments}
            className="tw-m-1"
          />
          <Label
            size="regular"
            className={classNames("tw-self-center  tw-select-none", {
              "tw-cursor-pointer": hasAttachments,
              "tw-opacity-50": !hasAttachments,
            })}
            htmlFor={hasAttachments ? "append-attachments" : undefined}
            weight="regular"
          >
            {t("Attachments")}
          </Label>
        </div>
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <Button
          type="submit"
          variant="primary"
          data-testid="rename-dialog-submit"
          data-qa-id="confirm-modal-submit"
          className="tw-mr-3"
          onClick={onAppend}
          disabled={!(form.append_body || form.append_attachments)}
        >
          {t("Append")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
        >
          {t("Cancel")}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

AppendDialog.propTypes = {
  open: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  id: PropTypes.number.isRequired,
};

AppendDialog.defaultProps = {
  open: false,
  onConfirm: () => null,
  onCancel: () => null,
};
